"use client";

import { useState, useRef } from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { uploadImages } from "@/lib/api/storage";
import { useAuth } from "@/lib/hooks/useAuth";
import { resizeToSquare } from "@/lib/utils/imageResize";
import { Upload, ChevronUp, ChevronDown, GripVertical } from "lucide-react";

import CategorySelector from "@/components/category/CategorySelector";
import StepType from "@/components/product/StepType";
import ProgressBar from "@/components/product/ProgressBar";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { CONDITION_GRADES } from "@/data/constants";

interface WizardFormData {
  category: string;
  categoryPath?: string[];
  categoryId?: string;
  tradeType: "sell" | "buy";
  productName: string;
  price: number;
  description: string;
  condition: string; // 상품 상태 (A, B, C, D)
  shippingTypes: string[];
  parcelPaymentType: string; // "seller" or "buyer"
  escrowEnabled: boolean;
  images: File[];
}

export default function ProductWizardPage() {
  const router = useRouter();
  const { user } = useAuth();
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedShippingTypes, setSelectedShippingTypes] = useState<string[]>(
    []
  );
  const [parcelPaymentType, setParcelPaymentType] = useState<string>(""); // "seller" or "buyer"
  const [escrowEnabled, setEscrowEnabled] = useState<boolean>(false); // 안전거래 옵션
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<WizardFormData>({
    category: "",
    tradeType: "",
    productName: "",
    price: null as any,
    description: "",
    condition: "A", // 기본값 A급
    shippingTypes: [],
    parcelPaymentType: "",
    escrowEnabled: false,
    images: [],
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<WizardFormData>({
    defaultValues: {
      category: "",
      tradeType: "",
      productName: "",
      price: null as any,
      description: "",
      condition: "A",
      shippingTypes: [],
      parcelPaymentType: "",
      escrowEnabled: false,
      images: [],
    },
  });

  const updateFormData = (newData: Partial<WizardFormData>) => {
    console.log("updateFormData 호출됨:", newData);
    console.log("현재 formData.price:", formData.price);

    // 가격은 항상 현재 값을 보존 (명시적으로 전달된 경우에만 변경)
    const { price, ...dataWithoutPrice } = newData;
    const updatedData = {
      ...formData,
      ...dataWithoutPrice,
      // 가격은 명시적으로 전달된 경우에만 변경, 그렇지 않으면 현재 값 유지
      price: price !== undefined ? price : formData.price,
    };

    setFormData(updatedData);

    // React Hook Form에도 업데이트 (가격 제외)
    Object.entries(dataWithoutPrice).forEach(([key, value]) => {
      console.log(`setValue 호출: ${key} = ${value}`);
      setValue(key as keyof WizardFormData, value, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    });

    console.log("updateFormData 완료 후 updatedData:", updatedData);
    console.log("보존된 가격:", updatedData.price);
  };

  const handleImageUpload = async (files: FileList) => {
    if (files.length === 0) return;

    setUploadingImages(true);
    try {
      const fileArray = Array.from(files);
      console.log(`이미지 업로드 시작: ${fileArray.length}개 파일`);

      // 이미지 자동 리사이징 (800x800, 품질 0.85로 개선)
      const resizedFiles = await Promise.all(
        fileArray.map(file => resizeToSquare(file, 800, 0.85))
      );

      // 리사이징만 하고, 실제 업로드는 onSubmit에서 수행
      const updatedImages = [...formData.images, ...resizedFiles];
      updateFormData({ images: updatedImages });

      console.log(`이미지 추가 완료: 총 ${updatedImages.length}장`);
      toast.success(
        `${fileArray.length}개의 이미지가 추가되었습니다. (총 ${updatedImages.length}장)`
      );
    } catch (error) {
      console.error("이미지 리사이징 실패:", error);
      toast.error("이미지 처리에 실패했습니다.");
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index: number) => {
    const updatedImages = formData.images.filter((_, i) => i !== index);
    updateFormData({ images: updatedImages });
  };

  const moveImage = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === formData.images.length - 1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const updatedImages = [...formData.images];
    [updatedImages[index], updatedImages[newIndex]] = [
      updatedImages[newIndex],
      updatedImages[index],
    ];
    updateFormData({ images: updatedImages });
  };

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updatedImages = [...formData.images];
    const draggedItem = updatedImages[draggedIndex];
    updatedImages.splice(draggedIndex, 1);
    updatedImages.splice(index, 0, draggedItem);
    updateFormData({ images: updatedImages });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const onSubmit = async (data: WizardFormData) => {
    try {
      console.log("상품 등록 시작 - 현재 사용자:", user);
      console.log("상품 등록 데이터:", data);
      console.log("로컬 상태 formData:", formData);
      console.log(
        "가격 비교 - data.price:",
        data.price,
        "formData.price:",
        formData.price
      );

      // 가격 검증
      if (!formData.price || formData.price <= 0) {
        alert("가격을 입력해주세요.");
        return;
      }

      // 사용자 로그인 확인
      if (!user) {
        toast.error("로그인이 필요합니다.");
        router.push("/auth/login");
        return;
      }

      // 이미지 업로드 (판매하기일 때만 필수)
      let imageUrls: string[] = [];
      if (formData.images.length > 0) {
        console.log("이미지 업로드 시작:", formData.images.length, "개 파일");
        console.log("🔍 현재 사용자:", user?.email || user?.uid);
        setUploadingImages(true);
        const uploadToast = toast.loading("이미지 업로드 중...", { id: "upload" });
        
        try {
          // 진행률 콜백으로 업로드 상태 표시
          const uploadResult = await uploadImages(
            formData.images,
            (index, progress) => {
              const totalProgress = ((index + 1) / formData.images.length) * 100;
              console.log(`이미지 ${index + 1}/${formData.images.length} 업로드 중... ${progress.percentage.toFixed(0)}%`);
            }
          );
          
          console.log("이미지 업로드 결과:", uploadResult);

          if (
            uploadResult.success &&
            uploadResult.urls &&
            uploadResult.urls.length > 0
          ) {
            imageUrls = [...uploadResult.urls]; // 배열 복사
            console.log("업로드된 이미지 URL들:", imageUrls);
            // 불필요한 메시지 제거
            // toast.success(`${imageUrls.length}개 이미지 업로드 완료!`, { id: "upload" });
          } else {
            console.error("이미지 업로드 실패:", uploadResult.errors);
            toast.error(
              `이미지 업로드에 실패했습니다: ${uploadResult.errors.join(", ")}`,
              { id: "upload" }
            );
            setUploadingImages(false);
            return;
          }
        } catch (error) {
          console.error("이미지 업로드 실패:", error);
          toast.error("이미지 업로드에 실패했습니다.", { id: "upload" });
          setUploadingImages(false);
          return;
        } finally {
          setUploadingImages(false);
        }
      } else {
        // 구매하기일 때는 이미지가 없어도 됨
        if (formData.tradeType === "sell") {
          console.log("판매하기인데 이미지가 없습니다.");
          toast.error("상품 사진을 최소 1장 이상 업로드해주세요.");
          return;
        } else {
          console.log("구매하기이므로 이미지가 없어도 됩니다.");
        }
      }

      // 실제 Firestore에 저장
      setUploadingImages(true); // 상품 저장 중에도 로딩 표시
      try {
        const { createItem } = await import("@/lib/api/products");

        // shippingTypes를 tradeOptions로 변환 (구매하기일 때는 빈 배열)
        const tradeOptions =
          formData.tradeType === "buy"
            ? []
            : data.shippingTypes.map(type => {
                switch (type) {
                  case "direct":
                    return "직거래";
                  case "parcel":
                    return "택배";
                  case "shipping":
                    return "화물운송";
                  case "escrow":
                    return "안전결제";
                  default:
                    return type;
                }
              });

        const itemData = {
          title: data.productName,
          description: data.description,
          category: data.category,
          price: formData.price, // 로컬 상태의 가격 사용
          region: user?.region || "지역 정보 없음", // 사용자 프로필의 지역 정보 사용
          condition: formData.tradeType === "buy" ? "구매" : formData.condition || "A", // 판매하기일 때는 선택한 상태 사용
          images: imageUrls, // 업로드된 이미지 URL들
          escrowEnabled:
            formData.tradeType === "sell" ? data.escrowEnabled : false, // 구매하기일 때는 false
          shippingTypes: formData.tradeType === "sell" ? data.shippingTypes : [], // 구매하기일 때는 빈 배열
          parcelPaymentType:
            formData.tradeType === "sell" ? data.parcelPaymentType : "", // 구매하기일 때는 빈 문자열
          sellerUid: user?.uid || "test-user", // 실제 로그인된 사용자 ID
          tradeOptions: tradeOptions,
          tradeType: formData.tradeType, // 구매/판매 구분을 위해 추가
        };

        console.log("저장할 상품 데이터:", itemData);
        console.log("imageUrls 변수:", imageUrls);
        console.log("imageUrls 타입:", typeof imageUrls);
        console.log("imageUrls 길이:", imageUrls.length);
        console.log("itemData.images:", itemData.images);
        console.log("itemData.images 타입:", typeof itemData.images);
        console.log("itemData.images 길이:", itemData.images?.length);

        const result = await createItem(itemData);

        if (result.success) {
          // 불필요한 메시지 제거
          // toast.success(
          //   formData.tradeType === "buy"
          //     ? "구매글이 성공적으로 등록되었습니다!"
          //     : "상품이 성공적으로 등록되었습니다!"
          // );

          // Firestore 인덱싱 대기 후 리다이렉트
          console.log("✅ 상품 등록 완료 - 1초 후 목록으로 이동");
          setTimeout(() => {
            router.push("/list");
          }, 1000);
        } else {
          toast.error(
            result.error ||
              (formData.tradeType === "buy"
                ? "구매글 등록에 실패했습니다."
                : "상품 등록에 실패했습니다.")
          );
        }
      } catch (error) {
        console.error("상품 저장 오류:", error);
        toast.error(
          formData.tradeType === "buy"
            ? "구매글 등록 중 오류가 발생했습니다."
            : "상품 등록 중 오류가 발생했습니다."
        );
        throw error; // 상위 catch로 전달
      } finally {
        setUploadingImages(false);
      }
    } catch (error) {
      console.error("상품 등록 오류:", error);
      toast.error(
        formData.tradeType === "buy"
          ? "구매글 등록 중 오류가 발생했습니다."
          : "상품 등록 중 오류가 발생했습니다."
      );
      setUploadingImages(false); // 최종적으로 false로 설정
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 헤더 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">상품 등록</h1>
          <p className="text-gray-600">단계별로 상품 정보를 입력해주세요</p>
        </div>

        {/* Progress Bar */}
        <ProgressBar
          currentStep={
            formData.tradeType && formData.category
              ? 3
              : formData.tradeType
                ? 2
                : 1
          }
          totalSteps={3}
        />

        {/* 메인 컨텐츠 */}
        <Card className="mt-8">
          <div className="p-6 sm:p-8">
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* 1단계 - 거래 유형 선택 (판매/구매) */}
              {!formData.tradeType && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <StepType
                    value={formData.tradeType as "sell" | "buy"}
                    onChange={value => updateFormData({ tradeType: value })}
                    onBack={() => {
                      // 홈으로 돌아가기
                      router.back();
                    }}
                  />
                </motion.div>
              )}

              {/* 2단계 - 카테고리 선택 (거래 유형 선택 후 나타남) */}
              {formData.tradeType && !formData.category && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="mt-12"
                >
                  <div className="border-t border-gray-200 pt-8">
                    <CategorySelector
                      onSelect={category => {
                        console.log(
                          "상품등록페이지: 카테고리 선택됨",
                          category
                        );
                        console.log(
                          "상품등록페이지: updateFormData 호출 전 formData:",
                          formData
                        );
                        updateFormData({
                          category: category.categoryPath
                            ? category.categoryPath.join(" > ")
                            : category.name,
                          categoryPath: category.categoryPath || [
                            category.name,
                          ],
                          categoryId: category.categoryId,
                        });
                        console.log("상품등록페이지: updateFormData 호출 후");
                      }}
                      onBack={() => {
                        // 거래 유형 선택 단계로 돌아가기
                        updateFormData({ tradeType: "" });
                      }}
                    />
                  </div>
                </motion.div>
              )}

              {/* 3단계 - 상품 등록 폼 (카테고리 선택 후 나타남) */}
              {formData.tradeType && formData.category && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  className="mt-12"
                >
                  <div className="border-t border-gray-200 pt-8">
                    <div className="space-y-8">
                      {/* 이전 버튼 */}
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={() => {
                            // 카테고리 선택 단계로 돌아가기
                            updateFormData({ category: "", productName: "" });
                          }}
                          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                          <span>이전</span>
                        </button>
                      </div>

                      <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                          {formData.tradeType === "buy"
                            ? "구매 정보를 입력해주세요"
                            : "상품 정보를 입력해주세요"}
                        </h2>
                        <p className="text-gray-600">
                          {formData.tradeType === "buy"
                            ? "구매하고 싶은 상품의 정보를 입력해주세요"
                            : "상품의 상세 정보를 입력해주세요"}
                        </p>
                      </div>

                      {/* 상품명 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {formData.tradeType === "buy"
                            ? "글 제목 *"
                            : "상품명 *"}
                        </label>
                        <input
                          type="text"
                          {...register("productName", {
                            required:
                              formData.tradeType === "buy"
                                ? "글 제목을 입력해주세요"
                                : "상품명을 입력해주세요",
                          })}
                          placeholder={
                            formData.tradeType === "buy"
                              ? "예: 야마하 디지털 피아노 구매합니다"
                              : "상품명을 입력해주세요"
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {errors.productName && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.productName.message}
                          </p>
                        )}
                      </div>

                      {/* 가격 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {formData.tradeType === "buy"
                            ? "희망가격 *"
                            : "판매가격 *"}
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={
                              formData.price && formData.price > 0
                                ? formData.price.toLocaleString()
                                : ""
                            }
                            onChange={e => {
                              const value = e.target.value.replace(
                                /[^0-9]/g,
                                ""
                              );
                              const priceValue = value
                                ? parseInt(value, 10)
                                : null;

                              console.log("가격 입력 변경:", {
                                value,
                                priceValue,
                                currentPrice: formData.price,
                              });

                              // 로컬 상태만 업데이트 (React Hook Form과 완전 분리)
                              setFormData(prev => {
                                const newData = { ...prev, price: priceValue };
                                console.log("가격 상태 업데이트:", {
                                  prev: prev.price,
                                  new: priceValue,
                                });
                                return newData;
                              });

                              // React Hook Form도 동기화 (하지만 UI는 로컬 상태 사용)
                              setValue("price", priceValue, {
                                shouldValidate: false,
                                shouldDirty: true,
                                shouldTouch: true,
                              });
                            }}
                            placeholder="예: 300,000"
                            className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                            원
                          </span>
                        </div>
                        {formData.price === null || formData.price === 0 ? (
                          <p className="mt-1 text-sm text-red-600">
                            가격을 입력해주세요
                          </p>
                        ) : null}
                      </div>

                      {/* 상품 상태 - 판매하기일 때만 표시 */}
                      {formData.tradeType === "sell" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            상품 상태 *
                          </label>
                          <select
                            value={formData.condition}
                            onChange={e => {
                              updateFormData({ condition: e.target.value });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            {CONDITION_GRADES.map(grade => (
                              <option key={grade.key} value={grade.key}>
                                {grade.label} - {grade.description}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            상품의 상태를 선택해주세요.
                          </p>
                        </div>
                      )}

                      {/* 판매 방법 - 판매하기일 때만 표시 */}
                      {formData.tradeType === "sell" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            판매 방법 * (여러 개 선택 가능)
                          </label>
                          <div className="flex flex-col sm:flex-row gap-3">
                            {[
                              { key: "direct", label: "직거래", icon: "🤝" },
                              { key: "parcel", label: "택배", icon: "📦" },
                            ].map(type => (
                              <div key={type.key} className="flex-1">
                                <label
                                  className="flex items-center justify-between p-4 border-2 border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105"
                                  onClick={() => {
                                    if (
                                      type.key === "parcel" &&
                                      selectedShippingTypes.includes(
                                        "parcel"
                                      ) &&
                                      parcelPaymentType
                                    ) {
                                      setParcelPaymentType("");
                                      updateFormData({ parcelPaymentType: "" });
                                    }
                                  }}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-5 h-5 flex items-center justify-center">
                                      <svg
                                        className={`w-4 h-4 ${
                                          selectedShippingTypes.includes(
                                            type.key
                                          )
                                            ? "text-blue-600 block"
                                            : "text-transparent hidden"
                                        }`}
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                    <span
                                      className={`text-base ${
                                        selectedShippingTypes.includes(type.key)
                                          ? "font-bold text-gray-900"
                                          : "font-medium text-gray-700"
                                      }`}
                                    >
                                      {type.key === "parcel" &&
                                      selectedShippingTypes.includes("parcel")
                                        ? parcelPaymentType === "seller"
                                          ? "택배 (판매자부담)"
                                          : "택배 (구매자부담)"
                                        : type.label}
                                    </span>
                                  </div>
                                  <input
                                    type="checkbox"
                                    value={type.key}
                                    checked={selectedShippingTypes.includes(
                                      type.key
                                    )}
                                    onChange={e => {
                                      const value = e.target.value;
                                      if (e.target.checked) {
                                        const newTypes = [
                                          ...selectedShippingTypes,
                                          value,
                                        ];
                                        setSelectedShippingTypes(newTypes);
                                        setValue("shippingTypes", newTypes);
                                        
                                        // 택배 선택 시 안전거래 옵션 비활성화 (임시)
                                        if (value === "parcel") {
                                          setEscrowEnabled(false);
                                          updateFormData({ escrowEnabled: false });
                                        }
                                      } else {
                                        const newTypes =
                                          selectedShippingTypes.filter(
                                            t => t !== value
                                          );
                                        setSelectedShippingTypes(newTypes);
                                        setValue("shippingTypes", newTypes);
                                        
                                        // 택배 해제 시에도 안전거래 옵션 비활성화 유지
                                        if (value === "parcel") {
                                          setEscrowEnabled(false);
                                          updateFormData({ escrowEnabled: false });
                                        }
                                      }
                                    }}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 opacity-0 absolute"
                                  />
                                </label>

                                {/* 택배 부담 방식 선택 */}
                                {type.key === "parcel" &&
                                  selectedShippingTypes.includes("parcel") &&
                                  !parcelPaymentType && (
                                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                      <div className="flex gap-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setParcelPaymentType("seller");
                                            updateFormData({
                                              parcelPaymentType: "seller",
                                            });
                                          }}
                                          className="flex-1 p-2 rounded-lg text-sm font-medium transition-all duration-200 bg-white text-blue-600 border border-blue-300 hover:bg-blue-100"
                                        >
                                          판매자 부담
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setParcelPaymentType("buyer");
                                            updateFormData({
                                              parcelPaymentType: "buyer",
                                            });
                                          }}
                                          className="flex-1 p-2 rounded-lg text-sm font-medium transition-all duration-200 bg-white text-blue-600 border border-blue-300 hover:bg-blue-100"
                                        >
                                          구매자 부담
                                        </button>
                                      </div>
                                    </div>
                                  )}
                              </div>
                            ))}
                          </div>

                          {/* 안전거래 옵션 - 택배 선택 시에만 표시 (임시 비활성화) */}
                          {selectedShippingTypes.includes("parcel") && (
                            <div
                              className={`mt-4 bg-gradient-to-br from-gray-50 to-gray-100 border-2 rounded-xl p-6 cursor-not-allowed transition-all duration-300 opacity-60 ${
                                escrowEnabled
                                  ? "border-gray-400"
                                  : "border-gray-300"
                              }`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // 임시로 비활성화 - 안전거래 기능이 아직 지원되지 않음
                                toast.info("안전거래 기능은 현재 준비 중입니다.");
                              }}
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center transition-all bg-gray-300"
                                  >
                                    <span className="text-2xl">
                                      🛡️
                                    </span>
                                  </div>
                                  <div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-lg font-bold text-gray-600">
                                        안전거래
                                      </span>
                                      <span className="bg-gray-400 text-white text-xs font-bold px-3 py-1 rounded-full">
                                        준비중
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1 font-medium">
                                      안전거래 기능은 현재 준비 중입니다. 곧 제공될 예정입니다.
                                    </p>
                                  </div>
                                </div>
                                <label className="flex items-center cursor-not-allowed opacity-50">
                                  <input
                                    type="checkbox"
                                    checked={false}
                                    disabled={true}
                                    onChange={e => {
                                      e.stopPropagation();
                                      // 비활성화됨
                                    }}
                                    className="w-8 h-8 text-gray-400 border-2 border-gray-300 rounded-lg focus:ring-gray-400 cursor-not-allowed"
                                  />
                                </label>
                              </div>
                              <div className="pt-3 border-t border-gray-200">
                                <p className="text-xs text-gray-500">
                                  안전거래 기능은 현재 준비 중입니다.
                                </p>
                              </div>
                            </div>
                          )}

                          {errors.shippingTypes && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.shippingTypes.message}
                            </p>
                          )}
                        </div>
                      )}

                      {/* 사진 업로드 - 판매하기일 때만 표시 */}
                      {formData.tradeType === "sell" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            상품 사진 * (최소 1장)
                          </label>

                          {/* 업로드된 이미지 미리보기 */}
                          {formData.images.length > 0 && (
                            <div className="space-y-3 mb-4">
                              {formData.images.map((file, index) => (
                                <div
                                  key={index}
                                  draggable
                                  onDragStart={() => handleDragStart(index)}
                                  onDragOver={e => handleDragOver(e, index)}
                                  onDragEnd={handleDragEnd}
                                  className={`relative group flex items-center gap-2 p-2 rounded-lg border-2 ${
                                    draggedIndex === index
                                      ? "border-blue-500 bg-blue-50"
                                      : "border-gray-200 bg-white"
                                  }`}
                                >
                                  {/* 드래그 핸들 (모바일) */}
                                  <div className="md:hidden flex-shrink-0 text-gray-400">
                                    <GripVertical className="w-5 h-5" />
                                  </div>

                                  {/* 이미지 */}
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={`상품 이미지 ${index + 1}`}
                                    className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                                  />

                                  {/* 순서 정보 */}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-700">
                                      이미지 {index + 1}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {file.name}
                                    </p>
                                  </div>

                                  {/* 순서 변경 버튼 (웹) */}
                                  <div className="hidden md:flex flex-col gap-1 flex-shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => moveImage(index, "up")}
                                      disabled={index === 0}
                                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <ChevronUp className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => moveImage(index, "down")}
                                      disabled={index === formData.images.length - 1}
                                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                      <ChevronDown className="w-4 h-4" />
                                    </button>
                                  </div>

                                  {/* 삭제 버튼 */}
                                  <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors flex-shrink-0"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* 이미지 업로드 영역 */}
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={e => {
                                if (e.target.files && e.target.files.length > 0) {
                                  console.log(`선택된 파일 개수: ${e.target.files.length}`);
                                  handleImageUpload(e.target.files);
                                  // 같은 파일을 다시 선택할 수 있도록 value 초기화
                                  e.target.value = "";
                                }
                              }}
                              className="hidden"
                              id="image-upload"
                            />
                            <label
                              htmlFor="image-upload"
                              className="cursor-pointer flex flex-col items-center"
                            >
                              {uploadingImages ? (
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                              ) : (
                                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                              )}
                              <span className="text-sm text-gray-600 font-medium mb-1">
                                {uploadingImages
                                  ? "이미지 처리 중..."
                                  : "사진 선택하기"}
                              </span>
                              <span className="text-xs text-gray-500">
                                {uploadingImages
                                  ? "잠시만 기다려주세요..."
                                  : "여러 장을 한 번에 선택할 수 있습니다"}
                              </span>
                              {formData.images.length > 0 && (
                                <span className="text-xs text-blue-600 mt-1">
                                  현재 {formData.images.length}장 선택됨
                                </span>
                              )}
                            </label>
                          </div>

                          {formData.images.length === 0 && (
                            <p className="mt-1 text-sm text-red-600">
                              최소 1장의 사진을 업로드해주세요
                            </p>
                          )}
                        </div>
                      )}

                      {/* 내용 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {formData.tradeType === "buy"
                            ? "내용 *"
                            : "판매 내용 *"}
                        </label>
                        <textarea
                          {...register("description", {
                            required:
                              formData.tradeType === "buy"
                                ? "내용을 입력해주세요"
                                : "판매 내용을 입력해주세요",
                          })}
                          placeholder={
                            formData.tradeType === "buy"
                              ? "구매하고 싶은 상품에 대한 자세한 설명을 입력해주세요"
                              : "상품에 대한 자세한 설명을 입력해주세요"
                          }
                          rows={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                          onKeyDown={(e) => {
                            // Ctrl/Cmd + Arrow Up/Down으로 페이지 스크롤
                            if ((e.ctrlKey || e.metaKey) && e.key === "ArrowUp") {
                              e.preventDefault();
                              window.scrollBy({ top: -200, behavior: "smooth" });
                            } else if ((e.ctrlKey || e.metaKey) && e.key === "ArrowDown") {
                              e.preventDefault();
                              window.scrollBy({ top: 200, behavior: "smooth" });
                            }
                          }}
                        />
                        {errors.description && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.description.message}
                          </p>
                        )}
                      </div>

                      {/* 등록 버튼 - 하단 고정 (모바일 Nav 고려) */}
                      <div className="fixed bottom-[72px] left-0 right-0 bg-white border-t border-gray-200 p-4 z-10 md:bottom-0 md:z-10">
                        <div className="max-w-4xl mx-auto">
                          <Button
                            type="submit"
                            className="w-full py-4 text-lg font-semibold"
                            disabled={uploadingImages}
                          >
                            {uploadingImages
                              ? "업로드 중..."
                              : formData.tradeType === "buy"
                                ? "구매글 등록 완료"
                                : "상품 등록 완료"}
                          </Button>
                        </div>
                      </div>

                      {/* 하단 여백 - 고정 버튼 및 모바일 Nav를 위한 공간 */}
                      <div className="h-40 md:h-20"></div>
                    </div>
                  </div>
                </motion.div>
              )}
            </form>
          </div>
        </Card>
      </div>
    </div>
  );
}
